import * as React from 'react';
import { useState, useEffect, useCallback } from 'react';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { TouchRippleActions } from '@mui/material/ButtonBase/TouchRipple';
import { Snackbar, Alert, IconButton } from '@mui/material';
import { FiTrash2 } from 'react-icons/fi';
import GridMui from '../../components/gridMui';
import api from '../../services/api';
import Loading from '../../components/loading';
import AlertDialogSlide from '../../components/alertDialog';
import {
  Container,
  Flex,
  Card,
  CardHeader,
  CardContent,
  Badge,
} from '../../components/ui';
import { useTheme } from '../../context/ThemeContext';

interface AlertState {
  open: boolean;
  message: string;
  severity: 'success' | 'info' | 'warning' | 'error';
}

const Users: React.FC = () => {
  const [data, setData] = useState<any[]>([]);
  const [column, setColumn] = useState<any[]>([]);
  const [confirmDelete, setConfirmDelete] = useState<any | null>(null);
  const [alertState, setAlertState] = useState<AlertState>({
    open: false,
    message: '',
    severity: 'info',
  });
  const { isDarkMode } = useTheme();

  const showAlert = (
    message: string,
    severity: AlertState['severity'],
  ): void => {
    setAlertState({ open: true, message, severity });
  };

  const closeAlert = (
    _e?: React.SyntheticEvent | Event,
    reason?: string,
  ): void => {
    if (reason === 'clickaway') return;
    setAlertState(prev => ({ ...prev, open: false }));
  };

  const handleDelete = useCallback((user: any): void => {
    api
      .delete(`/users/${user.email}`)
      .then(() => {
        showAlert('User deleted', 'success');
        setData(prev => prev.filter(u => u.email !== user.email));
      })
      .catch((error: any) => {
        const msg = error?.response?.data?.message ?? 'Error deleting user';
        showAlert(msg, 'error');
      });
  }, []);

  const muiTheme = createTheme({
    palette: {
      mode: isDarkMode ? 'dark' : 'light',
    },
    components: {
      // @ts-expect-error - MuiDataGrid is from @mui/x-data-grid
      MuiDataGrid: {
        styleOverrides: {
          root: {
            backgroundColor: isDarkMode ? '#1C1C1E' : '#FFFFFF',
            color: isDarkMode ? '#FFFFFF' : '#263238',
            border: `1px solid ${isDarkMode ? '#48484A' : '#E5E5EA'}`,
            '& .MuiDataGrid-cell': {
              color: isDarkMode ? '#FFFFFF' : '#263238',
              borderColor: isDarkMode ? '#48484A' : '#E5E5EA',
            },
            '& .MuiDataGrid-columnHeaders': {
              backgroundColor: isDarkMode ? '#2C2C2E' : '#F5F5F5',
              color: isDarkMode ? '#FFFFFF' : '#263238',
              borderColor: isDarkMode ? '#48484A' : '#E5E5EA',
            },
            '& .MuiDataGrid-columnHeader': {
              color: isDarkMode ? '#FFFFFF' : '#263238',
            },
            '& .MuiDataGrid-row': {
              backgroundColor: isDarkMode ? '#1C1C1E' : '#FFFFFF',
              '&:nth-of-type(even)': {
                backgroundColor: isDarkMode ? '#2C2C2E' : '#FAFAFA',
              },
              '&:hover': {
                backgroundColor: isDarkMode ? '#3A3A3C' : '#F0F0F0',
              },
            },
            '& .MuiDataGrid-footerContainer': {
              backgroundColor: isDarkMode ? '#2C2C2E' : '#F5F5F5',
              color: isDarkMode ? '#FFFFFF' : '#263238',
              borderColor: isDarkMode ? '#48484A' : '#E5E5EA',
            },
          },
        },
      },
    },
  });

  useEffect(() => {
    api
      .get('/users')
      .then((response: any) => {
        setData(response.data);
      })
      .catch((error: any) => {
        console.error(error.response.data.message);
      });
  }, []);

  const RenderValue = (
    props: GridRenderCellParams<string[]>,
  ): React.ReactNode => {
    const { hasFocus, value } = props;
    const buttonElement = React.useRef<HTMLButtonElement | null>(null);
    const rippleRef = React.useRef<TouchRippleActions | null>(null);

    React.useLayoutEffect(() => {
      if (hasFocus) {
        const input = buttonElement.current?.querySelector('input');
        input?.focus();
      } else if (rippleRef.current) {
        rippleRef.current.stop({} as any);
      }
    }, [hasFocus]);

    const tags = value?.map((v: any, index: number) => {
      return (
        <Badge key={index} variant="secondary" size="small">
          {v}
        </Badge>
      );
    });

    return (
      <Flex
        direction="row"
        wrap="wrap"
        gap={1}
        style={{
          padding: '4px 0',
          height: '100%',
        }}
        align="center"
      >
        {tags}
      </Flex>
    );
  };

  useEffect(() => {
    if (data.length > 0) {
      const columns: GridColDef<(typeof data)[number]>[] = [
        {
          field: 'name',
          headerName: 'Name',
          flex: 0.7,
          editable: false,
        },
        {
          field: 'email',
          headerName: 'Email',
          flex: 1,
          editable: false,
        },
        {
          field: 'usergroup',
          headerName: 'UserGroup',
          flex: 1,
          editable: false,
          renderCell: RenderValue,
        },
        {
          field: 'company',
          headerName: 'Company',
          flex: 1,
          editable: false,
          renderCell: RenderValue,
        },
        {
          field: 'dashboards',
          headerName: 'Dashboards',
          flex: 1,
          editable: false,
          renderCell: RenderValue,
        },
        {
          field: 'widgets',
          headerName: 'Widgets',
          flex: 1,
          editable: false,
          renderCell: RenderValue,
        },
        {
          field: 'roles',
          headerName: 'Roles',
          flex: 1,
          editable: false,
          renderCell: RenderValue,
        },
        {
          field: 'isGuest',
          headerName: 'Guest',
          flex: 0.5,
          editable: false,
          renderCell: params => (
            <Flex align="center" style={{ height: '100%' }}>
              {params.value ? 'Yes' : 'No'}
            </Flex>
          ),
        },
        {
          field: 'actions',
          headerName: '',
          width: 80,
          sortable: false,
          filterable: false,
          renderCell: params => (
            <Flex align="center" justify="center" style={{ height: '100%' }}>
              <IconButton
                size="small"
                onClick={() => setConfirmDelete(params.row)}
                aria-label="delete"
              >
                <FiTrash2 />
              </IconButton>
            </Flex>
          ),
        },
      ];

      setColumn(columns);
    }
  }, [data]);

  return (
    <Container maxWidth="full" padding>
      <Card variant="elevated" padding="large" fullHeight>
        <CardHeader>
          <h1 style={{ margin: 0 }}>Users</h1>
        </CardHeader>
        <CardContent>
          <ThemeProvider theme={muiTheme}>
            {data.length > 0 ? (
              <GridMui columns={column} rows={data} pageSizeOptions={20} />
            ) : (
              <Flex
                justify="center"
                align="center"
                style={{ minHeight: '400px' }}
              >
                <Loading />
              </Flex>
            )}
          </ThemeProvider>
        </CardContent>
      </Card>

      {confirmDelete && (
        <ThemeProvider theme={muiTheme}>
          <AlertDialogSlide
            title="Delete user"
            msg={`Are you sure you want to delete ${confirmDelete.email}?`}
            handleYes={() => {
              handleDelete(confirmDelete);
              setConfirmDelete(null);
            }}
            setShow={(show: boolean) => {
              if (!show) setConfirmDelete(null);
            }}
          />
        </ThemeProvider>
      )}

      <Snackbar
        open={alertState.open}
        autoHideDuration={5000}
        onClose={closeAlert}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={closeAlert}
          severity={alertState.severity}
          sx={{ width: '100%' }}
        >
          {alertState.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default Users;
